import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API, AUTH_URL } from '../config';

const Upload = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [propertyType, setPropertyType] = useState('home');
  const [orientation, setOrientation] = useState('north');
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);

  const propertyTypes = [
    { value: 'home', label: 'Home/Apartment' },
    { value: 'office', label: 'Office' },
    { value: 'land', label: 'Land Parcel' },
    { value: 'commercial', label: 'Commercial Space' },
    { value: 'factory', label: 'Factory/Industrial' },
  ];

  const orientations = [
    { value: 'north', label: 'North' },
    { value: 'south', label: 'South' },
    { value: 'east', label: 'East' },
    { value: 'west', label: 'West' },
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  const handleAnalyze = async () => {
    if (!file || !user) return;

    setAnalyzing(true);
    setProgress(0);
    
    // Safety timeout - navigate to editor even if something fails
    const safetyTimeout = setTimeout(() => {
      console.warn('Detection taking too long, navigating to editor...');
      setAnalyzing(false);
    }, 30000); // 30 seconds

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        clearTimeout(safetyTimeout);
        const base64String = reader.result.split(',')[1];
        
        setProgress(30);
        
        // Detect rooms using AI
        try {
          const response = await axios.post(
            `${API}/vastu/detect-rooms`,
            {
              floor_plan_base64: base64String,
              property_type: propertyType,
              orientation: orientation,
            },
            { withCredentials: true }
          );
          
          setProgress(80);

          const detectionPayload = response?.data?.rooms || {};
          const detectedRooms = Array.isArray(detectionPayload.rooms) ? detectionPayload.rooms : [];
          
          // Navigate to editor with detected rooms
          setTimeout(() => {
            navigate('/editor', {
              state: {
                floorPlanBase64: base64String,
                propertyType: propertyType,
                orientation: orientation,
                detectedRooms,
                detectionMeta: detectionPayload,
              },
            });
          }, 500);
        } catch (error) {
          console.error('Room detection error:', error);
          setProgress(100);
          // Still navigate to editor but with no pre-detected rooms
          setTimeout(() => {
            navigate('/editor', {
              state: {
                floorPlanBase64: base64String,
                propertyType: propertyType,
                orientation: orientation,
                detectedRooms: [],
                detectionMeta: {
                  detection_status: 'failed',
                  errors: ['Detection request failed before analysis completed.'],
                },
              },
            });
          }, 500);
        }
      };
      
      reader.onerror = (error) => {
        clearTimeout(safetyTimeout);
        console.error('File reading error:', error);
        alert('Failed to read floor plan file. Please try again.');
        setAnalyzing(false);
        setProgress(0);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      clearTimeout(safetyTimeout);
      console.error('Upload error:', error);
      alert('Failed to process floor plan. Please try again.');
      setAnalyzing(false);
      setProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#232323]" />
          <p className="text-[#353535]">Checking login status...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-bold mb-4 text-[#232323]">Login Required</h2>
          <p className="text-[#353535] mb-6">
            Please log in to upload and analyze your floor plan.
          </p>
          <a
            href={AUTH_URL ? `${AUTH_URL}/?redirect=${encodeURIComponent(window.location.href)}` : '/login'}
            className="btn-primary"
          >
            Login with Google
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-4 text-[#232323]">Upload Floor Plan</h1>
        <p className="text-center text-[#353535] mb-8">
          Upload your floor plan for AI-powered Vastu analysis
        </p>

        {/* Floor Plan Type Guidance */}
        <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-200 flex gap-3 text-blue-900 text-sm">
          <span className="text-lg">ℹ️</span>
          <div>
            <span className="font-semibold">Supported Floor Plans:</span> For best accuracy, please upload clean 2D architectural blueprint drawings (like CAD diagrams). Hand-drawn sketches, photos of physical prints, or 3D renders may not be parsed as accurately.
          </div>
        </div>

        {/* Upload Area */}
        <div
          className="border-2 border-dashed border-[#999999] rounded-xl p-12 text-center mb-8 bg-white hover:border-[#4D4D4D] transition-colors cursor-pointer"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('fileInput').click()}
        >
          {preview ? (
            <div className="space-y-4">
              <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
              <p className="text-sm text-[#353535]">Click to change image</p>
            </div>
          ) : (
            <div className="space-y-4">
              <UploadIcon className="w-16 h-16 mx-auto text-[#999999]" />
              <p className="text-lg text-[#232323]">Drag & drop your floor plan here</p>
              <p className="text-sm text-[#353535]">or click to browse files</p>
              <p className="text-xs text-[#999999]">Supports: JPG, PNG, PDF</p>
            </div>
          )}
          <input
            id="fileInput"
            type="file"
            accept="image/*,application/pdf,.pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Property Type Selector */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-3 text-[#232323]">
            Property Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {propertyTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setPropertyType(type.value)}
                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                  propertyType === type.value
                    ? 'border-[#232323] bg-[#232323] text-white'
                    : 'border-[#999999] bg-white text-[#232323] hover:border-[#4D4D4D]'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Orientation Selector */}
        <div className="mb-8">
          <label className="block text-sm font-semibold mb-3 text-[#232323]">
            Main Entrance Direction
          </label>
          <div className="grid grid-cols-4 gap-3">
            {orientations.map((dir) => (
              <button
                key={dir.value}
                onClick={() => setOrientation(dir.value)}
                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                  orientation === dir.value
                    ? 'border-[#232323] bg-[#232323] text-white'
                    : 'border-[#999999] bg-white text-[#232323] hover:border-[#4D4D4D]'
                }`}
              >
                {dir.label}
              </button>
            ))}
          </div>
        </div>

        {/* Analysis Button */}
        {analyzing ? (
          <div className="bg-white p-8 rounded-xl text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-[#232323]" />
            <p className="text-lg font-semibold mb-2 text-[#232323]">Detecting Rooms with AI...</p>
            <p className="text-sm text-[#353535] mb-4">Our AI is analyzing your floor plan</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#232323] h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        ) : (
          <button
            onClick={handleAnalyze}
            disabled={!file}
            className={`btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 ${
              !file ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {file ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Detect Rooms & Continue
              </>
            ) : (
              'Upload floor plan to continue'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default Upload;
